import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trash2, Plus, Eye, EyeOff, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { item: "", vendor: "", category: "", room: "", quantity: "1", trade_cost: "", markup: "" };

export default function PurchasesSection({ projectId, readOnly, purchases, reload, hideCols, project, onProjectUpdate }) {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);

  const items = purchases || [];
  const load = reload || (() => {});

  const clientHidesTrade = !!project?.client_permissions?.budget?.hide_trade_markup;

  const toggleHide = async () => {
    try {
      const perms = { ...(project?.client_permissions || {}) };
      perms.budget = { ...(perms.budget || { view: true, edit: false }), hide_trade_markup: !clientHidesTrade };
      await api.put(`/projects/${projectId}/client-permissions`, { client_permissions: perms });
      onProjectUpdate && onProjectUpdate();
      toast.success(!clientHidesTrade ? "Trade/Markup hidden from client" : "Trade/Markup visible to client");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const clientPriceOf = (p) =>
    p.client_price != null ? p.client_price : (p.quantity || 0) * (p.trade_cost || 0) * (1 + (p.markup || 0) / 100);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/purchases`, {
        item: form.item,
        vendor: form.vendor || "",
        category: form.category || "General",
        room: form.room || "",
        quantity: parseInt(form.quantity) || 0,
        trade_cost: parseFloat(form.trade_cost) || 0,
        markup: parseFloat(form.markup) || 0,
      });
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      item: p.item || "", vendor: p.vendor || "", category: p.category || "", room: p.room || "",
      quantity: String(p.quantity ?? 1), trade_cost: String(p.trade_cost ?? ""), markup: String(p.markup ?? ""),
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(EMPTY); };

  const saveEdit = async (id) => {
    try {
      await api.put(`/purchases/${id}`, {
        item: editForm.item,
        vendor: editForm.vendor || "",
        category: editForm.category || "General",
        room: editForm.room || "",
        quantity: parseInt(editForm.quantity) || 0,
        trade_cost: parseFloat(editForm.trade_cost) || 0,
        markup: parseFloat(editForm.markup) || 0,
      });
      cancelEdit();
      load();
      toast.success("Purchase updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/purchases/${id}`); load(); };

  const grouped = items.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});
  const grandTotal = items.reduce((s, p) => s + clientPriceOf(p), 0);
  const tradeNetTotal = items.reduce((s, p) => s + (p.quantity || 0) * (p.trade_cost || 0), 0);
  const profit = grandTotal - tradeNetTotal;
  const marginPct = grandTotal ? Math.round((profit / grandTotal) * 100) : 0;
  const categories = Object.keys(grouped).sort();
  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const livePreview = (parseInt(form.quantity) || 0) * (parseFloat(form.trade_cost) || 0) * (1 + (parseFloat(form.markup) || 0) / 100);
  const editPreview = (parseInt(editForm.quantity) || 0) * (parseFloat(editForm.trade_cost) || 0) * (1 + (parseFloat(editForm.markup) || 0) / 100);
  const inputCls = "h-9 rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]";

  const colSpan = 2 + (hideCols ? 0 : 2) + 1 + (!readOnly ? 1 : 0);

  return (
    <div className="max-w-4xl space-y-6 pt-10 mt-10 border-t border-[#EBE5E0]" data-testid="purchases-section">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Itemized</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Purchases</h3>
        </div>
        <div className="text-right flex items-end gap-4">
          {!readOnly && (
            <button data-testid="toggle-hide-cols" onClick={toggleHide} type="button"
              title="Controls whether your client sees Trade/Net cost and Markup. You always see them."
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B] border border-[#EBE5E0] hover:border-[#A68A6B] px-3 py-2">
              {clientHidesTrade ? <Eye size={14} strokeWidth={1.5} /> : <EyeOff size={14} strokeWidth={1.5} />}
              {clientHidesTrade ? "Show Trade/Markup to client" : "Hide Trade/Markup from client"}
            </button>
          )}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Total Client Price</p>
            <p data-testid="purchases-grand-total" className="font-serif text-3xl text-[#2B2622]">{fmt(grandTotal)}</p>
          </div>
          {!readOnly && (
            <div data-testid="purchases-trade-total-block">
              <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Total Trade/Net Price</p>
              <p data-testid="purchases-trade-total" className="font-serif text-3xl text-[#5C5651]">{fmt(tradeNetTotal)}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">You & your team only</p>
            </div>
          )}
          {!readOnly && (
            <div data-testid="purchases-profit-block">
              <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Profit / Margin</p>
              <p data-testid="purchases-profit" className="font-serif text-3xl text-[#506B52]">{fmt(profit)}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">{marginPct}% margin · you & team only</p>
            </div>
          )}
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-10 text-center text-[#5C5651]">
          No purchases recorded yet.
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map((cat) => {
            const rows = grouped[cat];
            const subtotal = rows.reduce((s, r) => s + clientPriceOf(r), 0);
            return (
              <div key={cat} className="border border-[#EBE5E0] bg-white overflow-x-auto" data-testid={`purchase-category-${cat}`}>
                <div className="flex items-center justify-between px-5 py-3 bg-[#F9F8F6] border-b border-[#EBE5E0]">
                  <span className="text-xs uppercase tracking-wider text-[#A68A6B]">{cat}</span>
                  <span className="text-sm text-[#2B2622]">{fmt(subtotal)}</span>
                </div>
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[#EBE5E0] text-left text-[11px] uppercase tracking-wider text-[#A68A6B]">
                      <th className="px-5 py-2.5 font-medium">Item</th>
                      <th className="px-3 py-2.5 font-medium text-right">Qty</th>
                      {!hideCols && <th className="px-3 py-2.5 font-medium text-right">Trade/Net</th>}
                      {!hideCols && <th className="px-3 py-2.5 font-medium text-right">Markup</th>}
                      <th className="px-5 py-2.5 font-medium text-right">Client Price</th>
                      {!readOnly && <th className="px-3 py-2.5 w-16"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      editingId === p.id ? (
                        <tr key={p.id} className="border-b border-[#F2EFEB] last:border-0 bg-[#FBFAF8]" data-testid="purchase-row-editing">
                          <td className="px-5 py-3 align-top" colSpan={colSpan}>
                            <div className="grid sm:grid-cols-2 gap-2 mb-2">
                              <Input data-testid={`edit-purchase-item-${p.id}`} placeholder="Item name" value={editForm.item}
                                onChange={(e) => setEditForm({ ...editForm, item: e.target.value })} className={inputCls} />
                              <Input data-testid={`edit-purchase-vendor-${p.id}`} placeholder="Vendor" value={editForm.vendor}
                                onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} className={inputCls} />
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                              <Input data-testid={`edit-purchase-category-${p.id}`} placeholder="Category" value={editForm.category}
                                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className={inputCls} />
                              <Input data-testid={`edit-purchase-room-${p.id}`} placeholder="Room" value={editForm.room}
                                onChange={(e) => setEditForm({ ...editForm, room: e.target.value })} className={inputCls} />
                              <Input data-testid={`edit-purchase-qty-${p.id}`} type="number" min="1" placeholder="Qty" value={editForm.quantity}
                                onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} className={inputCls} />
                              <Input data-testid={`edit-purchase-trade-${p.id}`} type="number" step="0.01" placeholder="Trade/Net" value={editForm.trade_cost}
                                onChange={(e) => setEditForm({ ...editForm, trade_cost: e.target.value })} className={inputCls} />
                              <Input data-testid={`edit-purchase-markup-${p.id}`} type="number" step="0.01" placeholder="Markup %" value={editForm.markup}
                                onChange={(e) => setEditForm({ ...editForm, markup: e.target.value })} className={inputCls} />
                            </div>
                            <div className="flex items-center justify-between gap-3 mt-3">
                              <span className="text-sm text-[#5C5651]">Client Price: <span className="text-[#2B2622] font-medium">{fmt(editPreview)}</span></span>
                              <div className="flex items-center gap-2">
                                <Button data-testid={`save-purchase-${p.id}`} type="button" onClick={() => saveEdit(p.id)}
                                  className="h-9 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-4">
                                  <Check size={14} className="mr-1" /> Save
                                </Button>
                                <Button data-testid={`cancel-purchase-${p.id}`} type="button" variant="ghost" onClick={cancelEdit}
                                  className="h-9 rounded-none text-[#5C5651] hover:text-[#9E4747] uppercase tracking-wider text-xs">
                                  <X size={14} className="mr-1" /> Cancel
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={p.id} className="border-b border-[#F2EFEB] last:border-0" data-testid="purchase-row">
                          <td className="px-5 py-3 text-[#2B2622]">
                            {p.item}
                            {(p.vendor || p.room) && (
                              <span className="block text-xs text-[#9b938b] mt-0.5">
                                {[p.vendor, p.room].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right text-[#2B2622]">{p.quantity ?? 1}</td>
                          {!hideCols && <td className="px-3 py-3 text-right text-[#2B2622]">{fmt(p.trade_cost)}</td>}
                          {!hideCols && <td className="px-3 py-3 text-right text-[#5C5651]">{(p.markup || 0)}%</td>}
                          <td className="px-5 py-3 text-right text-[#2B2622] font-medium">{fmt(clientPriceOf(p))}</td>
                          {!readOnly && (
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <button data-testid={`edit-purchase-${p.id}`} onClick={() => startEdit(p)}
                                className="text-[#5C5651] hover:text-[#A68A6B] mr-3"><Pencil size={15} strokeWidth={1.5} /></button>
                              <button data-testid={`delete-purchase-${p.id}`} onClick={() => remove(p.id)}
                                className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                            </td>
                          )}
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <form onSubmit={add} className="border border-[#EBE5E0] bg-[#F9F8F6] p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="purchase-item-input" placeholder="Item name" required value={form.item}
              onChange={(e) => setForm({ ...form, item: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            <Input data-testid="purchase-vendor-input" placeholder="Vendor" value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Input data-testid="purchase-category-input" placeholder="Category" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            <Input data-testid="purchase-room-input" placeholder="Room" value={form.room}
              onChange={(e) => setForm({ ...form, room: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            <Input data-testid="purchase-qty-input" type="number" min="1" placeholder="Qty" value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            <Input data-testid="purchase-trade-input" type="number" step="0.01" placeholder="Trade/Net cost" value={form.trade_cost}
              onChange={(e) => setForm({ ...form, trade_cost: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            <Input data-testid="purchase-markup-input" type="number" step="0.01" placeholder="Markup %" value={form.markup}
              onChange={(e) => setForm({ ...form, markup: e.target.value })}
              className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[#5C5651]">Client Price: <span data-testid="purchase-live-price" className="text-[#2B2622] font-medium">{fmt(livePreview)}</span></span>
            <Button data-testid="add-purchase-btn" type="submit"
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-6">
              <Plus size={15} className="mr-1" /> Add Purchase
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
